import type { ButtonClickEvent } from '@hc-frontend/core-ui';
import { useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import { toPhoneE164 } from '@hc-frontend/core-utils-format';
import type { ComponentImpressionEvent } from '@hc-frontend/features-tracking';
import type {
  SendMetricEvent,
  SendMetricEventTarget,
} from '@hc-frontend/shells-shared';
import {
  type MouseEvent,
  MouseEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { useUccModalDataContext } from './modal-data-provider';
import { useUccModalContext } from './modal-provider';

/**
 * Common set of utils/methods between all modal ui implementations.
 */
export function useModalCommonUtils() {
  const {
    id,
    metricName,
    experience,
    placement,
    product,
    autoTrigger,
    autoTriggerDelay,
    primaryCampaign,
  } = useUccModalDataContext();

  const ref = useUccModalContext();
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const internalRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitCall, setIsInitCall] = useState(false);

  const closeId = `${id}-close`;
  const optionCloseId = `${id}-option-close`;
  const connectMeButtonId = `${id}-connect-me-button`;
  const clickToCallButtonId = `${id}-click-to-call-button`;
  const clickToCallLinkId = `${id}-click-to-call-link`;

  const primaryCampaignPhoneNumber = primaryCampaign?.phoneNumber;
  const primaryCampaignPlacement = primaryCampaign?.placement;

  const connectMeDestinationPhoneNumber = primaryCampaignPhoneNumber
    ? toPhoneE164(primaryCampaignPhoneNumber)
    : undefined;

  const handleOpen = useCallback(() => {
    if (internalRef.current) {
      setIsOpen(true);
      internalRef.current.showModal();

      // Only send metric if primary campaign placementis present
      if (primaryCampaignPlacement) {
        eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
          id,
          topic: 'metric.client.message',
          source: id,
          metric: {
            name: metricName,
            tags: {
              action: 'Display',
              experience,
              product,
              placement: primaryCampaignPlacement,
            },
          },
          contexts,
        });
      }

      eventBus.publish<ComponentImpressionEvent>({
        id,
        topic: 'core.ui.component.impression',
        source: internalRef.current,
        componentImpression: {
          component: id,
          action: 'display',
          category: placement,
          product,
        },
        contexts,
      });
    }
  }, [
    id,
    metricName,
    eventBus,
    contexts,
    placement,
    primaryCampaignPlacement,
    experience,
    product,
  ]);

  const handleClose = useCallback(
    ({
      elementId,
      action = 'Close',
    }: {
      elementId: string;
      action?: string;
    }) => {
      if (internalRef.current) {
        eventBus.publish<ComponentImpressionEvent>({
          id: elementId,
          topic: 'core.ui.component.impression',
          source: internalRef.current,
          componentImpression: {
            component: id,
            action: 'hide',
            category: placement,
            product,
          },
          contexts,
        });

        // Only send metric if primary campaign placement is present
        if (primaryCampaignPlacement) {
          eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
            id: elementId,
            topic: 'metric.client.message',
            source: elementId,
            metric: {
              name: metricName,
              tags: {
                action,
                experience,
                product,
                placement: primaryCampaignPlacement,
              },
            },
            contexts,
          });
        }

        setIsOpen(false);
        internalRef.current.close();
      }
    },
    [
      contexts,
      eventBus,
      experience,
      id,
      metricName,
      placement,
      primaryCampaignPlacement,
      product,
    ],
  );

  const handleOnClickConnectMe = () => {
    setIsInitCall(true);
  };

  const handleOnOptionClose = (event: MouseEvent<HTMLButtonElement>) => {
    eventBus.publish<ButtonClickEvent>({
      id: optionCloseId,
      topic: 'core.ui.button.click',
      source: event.currentTarget,
      contexts,
    });

    handleClose({ elementId: optionCloseId });
  };

  const handleOnClickClose = (event: MouseEvent<HTMLButtonElement>) => {
    eventBus.publish<ButtonClickEvent>({
      id: closeId,
      topic: 'core.ui.button.click',
      source: event.currentTarget,
      contexts,
    });

    handleClose({ elementId: closeId });
  };

  const handleOnClickBackdrop: MouseEventHandler<HTMLDialogElement> = (
    event,
  ) => {
    if (event.target === internalRef.current) {
      handleClose({ elementId: closeId });
    }
  };

  // Automatically open the modal
  useEffect(() => {
    if (autoTrigger) {
      setTimeout(handleOpen, autoTriggerDelay);
    }
  }, [autoTrigger, autoTriggerDelay, handleOpen]);

  // Expose open method to pragmatically show the modal (e.g. a click action)
  useImperativeHandle(
    ref,
    () => ({
      showModal: handleOpen,
      hideModal: handleClose,
    }),
    [handleOpen, handleClose],
  );

  return {
    internalRef,
    isOpen,
    isInitCall,
    closeId,
    optionCloseId,
    connectMeButtonId,
    clickToCallButtonId,
    clickToCallLinkId,
    connectMeDestinationPhoneNumber,
    handleOpen,
    handleClose,
    handleOnClickBackdrop,
    handleOnClickClose,
    handleOnOptionClose,
    handleOnClickConnectMe,
  };
}
